import apiClient from '@/api/client'

// Architect Page (directives/Architect Page.md). Backend: app/api/v1/architect.py, flag_rules.py, messenger.py.

export interface ArchParish {
  id: string; name: string; sharecode: string
  address?: string | null; city?: string | null; state?: string | null; zip_code?: string | null
  primary_admin_id?: string | null; primary_admin_name?: string | null; primary_admin_email?: string | null
  subscription_status: string; subscription_expires_at?: string | null; auto_renew: boolean
  is_locked: boolean; locked_reason?: string | null
  is_deleted: boolean; deleted_at?: string | null; purge_on?: string | null
  created_at?: string | null; users: number; students: number; classes: number
}

export interface ArchPrimaryAdmin {
  user_id: string; first_name?: string | null; last_name?: string | null; email: string
  account_number?: string | null; user_status: string; created_at?: string | null; last_login?: string | null
  parish_id?: string | null; parish_name?: string | null; sharecode?: string | null
  subscription_status?: string | null; parish_locked: boolean; parish_deleted: boolean
}

export interface ArchLog {
  id: string; at: string; action: string; description: string
  user_id?: string | null; user_email?: string | null; user_account?: string | null
  target_user_id?: string | null; parish_id?: string | null; ip?: string | null
}

export interface ArchMetrics {
  as_of: string; parishes_registered: number; subscriptions_active: number; parishes_auto_renew: number
  parishes_locked: number; users_by_role: Record<string, number>; students: number; class_events: number
  role_descriptions: Record<string, number>
  subscriptions_forever_free: number | null; subscriptions_first_year_free: number | null
  subscriptions_first_year_discounted: number | null
}

export interface ArchDaily {
  from: string; to: string
  days: Array<{ date: string; parish_registrations: number; user_registrations: number; class_events_created: number }>
  not_tracked: string[]
}

export interface FlagRule {
  id: string; name: string; description?: string | null; keywords: string[]; match_type: string; priority: string
  auto_flag_enabled: boolean; notify_architect: boolean; applies_to_roles?: string[] | null
  applies_to_parish_id?: string | null; is_active: boolean; created_at: string; updated_at: string
}

export interface MessageFlag {
  id: string; parish_id?: string | null; message_id: string; event: string
  conversation_id?: string | null; conversation_name?: string | null
  sender_user_id?: string | null; sender_name?: string | null; body: string
  rule_id?: string | null; rule_name?: string | null; keyword?: string | null; priority?: string | null
  message_created_at?: string | null; flagged_at?: string | null; reviewed_at?: string | null
}

/** Save a file the API returns (needs the auth header, so no plain link). */
async function download(url: string, fallbackName: string) {
  const res = await apiClient.get(url, { responseType: 'blob' })
  const cd = String(res.headers['content-disposition'] || '')
  const name = /filename="([^"]+)"/.exec(cd)?.[1] || fallbackName
  const href = URL.createObjectURL(res.data as Blob)
  const a = document.createElement('a')
  a.href = href; a.download = name; document.body.appendChild(a); a.click(); a.remove()
  setTimeout(() => URL.revokeObjectURL(href), 1000)
}

const A = '/api/v1/architect'
export const architectApi = {
  parishes: async (): Promise<ArchParish[]> => (await apiClient.get(`${A}/parishes`)).data,
  lock: async (id: string, reason?: string) => (await apiClient.post(`${A}/parishes/${id}/lock`, { reason: reason || null })).data,
  unlock: async (id: string) => (await apiClient.post(`${A}/parishes/${id}/unlock`)).data,
  deleteLater: async (id: string) => (await apiClient.post(`${A}/parishes/${id}/delete-later`)).data,
  undelete: async (id: string) => (await apiClient.post(`${A}/parishes/${id}/undelete`)).data,
  purge: async (id: string, confirm: string) => (await apiClient.post(`${A}/parishes/${id}/purge`, { confirm })).data,
  exportParish: (id: string) => download(`${A}/parishes/${id}/export`, 'parish.json'),
  exportAddresses: () => download(`${A}/parishes/addresses.csv`, 'parish-addresses.csv'),
  primaryAdmins: async (): Promise<ArchPrimaryAdmin[]> => (await apiClient.get(`${A}/primary-admins`)).data,
  resetPassword: async (userId: string): Promise<{ temporary_password: string }> =>
    (await apiClient.post(`${A}/users/${userId}/reset-password`)).data,
  settings: async (): Promise<{ contact_email: string | null; contact_email_default: string }> => (await apiClient.get(`${A}/settings`)).data,
  saveSettings: async (contact_email: string | null) => (await apiClient.put(`${A}/settings`, { contact_email })).data,
  logs: async (params: { q?: string; limit?: number }): Promise<ArchLog[]> => (await apiClient.get(`${A}/logs`, { params })).data,
  metrics: async (as_of?: string): Promise<ArchMetrics> => (await apiClient.get(`${A}/metrics`, { params: as_of ? { as_of } : {} })).data,
  daily: async (from_date?: string, to_date?: string): Promise<ArchDaily> =>
    (await apiClient.get(`${A}/metrics/daily`, { params: { ...(from_date ? { from_date } : {}), ...(to_date ? { to_date } : {}) } })).data,
  flagRules: async (): Promise<FlagRule[]> => (await apiClient.get('/api/v1/flag-rules')).data,
  createFlagRule: async (r: Partial<FlagRule>) => (await apiClient.post('/api/v1/flag-rules', r)).data,
  updateFlagRule: async (id: string, r: Partial<FlagRule>) => (await apiClient.put(`/api/v1/flag-rules/${id}`, r)).data,
  deleteFlagRule: async (id: string) => (await apiClient.delete(`/api/v1/flag-rules/${id}`)).data,
  flags: async (unreviewed_only = true): Promise<MessageFlag[]> =>
    (await apiClient.get('/api/v1/messenger/flags', { params: { unreviewed_only } })).data,
  reviewFlag: async (id: string) => (await apiClient.post(`/api/v1/messenger/flags/${id}/review`)).data,
}
